import {
  userErrorMessage,
  authMutationResponseSchema,
  authSessionResponseSchema,
  type AuthSessionResponse,
} from '@wx-weiqi/contracts';

import { nextRequestId, WebApiError } from '../services/http';
import type { DownloadedFile, HttpService, RequestOptions, ResponseSchema } from '../services/http';
import { secureRandomUuid } from '../services/random-id';

const STORAGE_KEY = 'wx-weiqi.web.session.v1';
const DEVICE_KEY = 'wx-weiqi.web.device.v1';

export interface IdentityState {
  readonly session: AuthSessionResponse | null;
  readonly pending: boolean;
  readonly error: string | null;
}

type Listener = () => void;

function initialState(): IdentityState {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    if (value === null) return { session: null, pending: false, error: null };
    const parsed = authSessionResponseSchema.safeParse(JSON.parse(value) as unknown);
    if (parsed.success) return { session: parsed.data, pending: false, error: null };
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // An unavailable or corrupt session store must not prevent account login.
  }
  return { session: null, pending: false, error: null };
}

function deviceId(): string {
  try {
    const existing = sessionStorage.getItem(DEVICE_KEY);
    if (existing !== null) return existing;
  } catch {
    // An unavailable session store must not prevent account login.
  }
  const created = secureRandomUuid();
  try {
    sessionStorage.setItem(DEVICE_KEY, created);
  } catch {
    // The in-memory device identifier remains usable when storage is unavailable.
  }
  return created;
}

export class IdentityStore {
  private stateValue = initialState();
  private readonly listeners = new Set<Listener>();
  private refreshOperation: Promise<string> | null = null;
  private authorizationEpoch = 0;

  constructor(private readonly http: HttpService) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): IdentityState => this.stateValue;

  async login(username: string, password: string): Promise<void> {
    this.update({ ...this.stateValue, pending: true, error: null });
    try {
      const response = await this.http.request('/auth/account/login', authSessionResponseSchema, {
        method: 'POST',
        requestId: nextRequestId('web-login'),
        body: { username: username.trim(), password, deviceId: deviceId() },
      });
      this.accept(response);
    } catch (error: unknown) {
      this.fail(error);
      throw error;
    }
  }

  async selectMembership(membershipId: string): Promise<void> {
    const session = this.requireSession();
    const authorizationEpoch = this.authorizationEpoch;
    this.update({ ...this.stateValue, pending: true, error: null });
    try {
      const response = await this.http.request(
        '/auth/switch-membership',
        authSessionResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-membership'),
          body: { refreshToken: session.data.tokens.refreshToken, membershipId },
        },
      );
      this.assertAuthorizationEpoch(authorizationEpoch);
      this.accept(response);
    } catch (error: unknown) {
      if (authorizationEpoch === this.authorizationEpoch) this.fail(error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    const token = this.stateValue.session?.data.tokens.accessToken;
    // Invalidate the local identity before waiting for the network. This prevents
    // requests started by the previous account from being accepted while logout
    // is still in flight.
    this.clear();
    if (token !== null && token !== undefined) {
      await this.http
        .request('/auth/logout', authMutationResponseSchema, {
          method: 'POST',
          accessToken: token,
          requestId: nextRequestId('web-logout'),
        })
        .catch(() => undefined);
    }
  }

  async authorized<Response>(
    path: string,
    schema: ResponseSchema<Response>,
    options: Omit<RequestOptions, 'accessToken'> = {},
  ): Promise<Response> {
    const authorizationEpoch = this.authorizationEpoch;
    let accessToken = await this.ensureAccessToken();
    this.assertAuthorizationEpoch(authorizationEpoch);
    try {
      const response = await this.http.request(path, schema, { ...options, accessToken });
      this.assertAuthorizationEpoch(authorizationEpoch);
      return response;
    } catch (error: unknown) {
      if (!(error instanceof WebApiError) || error.status !== 401) throw error;
      this.assertAuthorizationEpoch(authorizationEpoch);
      accessToken = await this.refresh();
      this.assertAuthorizationEpoch(authorizationEpoch);
      const response = await this.http.request(path, schema, { ...options, accessToken });
      this.assertAuthorizationEpoch(authorizationEpoch);
      return response;
    }
  }

  async authorizedDownload(path: string, fallbackFilename: string): Promise<DownloadedFile> {
    const authorizationEpoch = this.authorizationEpoch;
    let accessToken = await this.ensureAccessToken();
    this.assertAuthorizationEpoch(authorizationEpoch);
    try {
      const file = await this.http.download(path, fallbackFilename, {
        accessToken,
        requestId: nextRequestId('web-download'),
      });
      this.assertAuthorizationEpoch(authorizationEpoch);
      return file;
    } catch (error: unknown) {
      if (!(error instanceof WebApiError) || error.status !== 401) throw error;
      this.assertAuthorizationEpoch(authorizationEpoch);
      accessToken = await this.refresh();
      this.assertAuthorizationEpoch(authorizationEpoch);
      const file = await this.http.download(path, fallbackFilename, {
        accessToken,
        requestId: nextRequestId('web-download-retry'),
      });
      this.assertAuthorizationEpoch(authorizationEpoch);
      return file;
    }
  }

  async ensureAccessToken(): Promise<string> {
    const tokens = this.requireSession().data.tokens;
    if (
      tokens.accessToken !== null &&
      tokens.accessTokenExpiresAt !== null &&
      Date.parse(tokens.accessTokenExpiresAt) > Date.now() + 30_000
    ) {
      return tokens.accessToken;
    }
    return this.refresh();
  }

  renewAccessToken(): Promise<string> {
    return this.refresh();
  }

  private refresh(): Promise<string> {
    if (this.refreshOperation !== null) return this.refreshOperation;
    const operation = this.performRefresh();
    const wrappedOperation = operation.finally(() => {
      if (this.refreshOperation === wrappedOperation) this.refreshOperation = null;
    });
    this.refreshOperation = wrappedOperation;
    return wrappedOperation;
  }

  private async performRefresh(): Promise<string> {
    const authorizationEpoch = this.authorizationEpoch;
    const refreshToken = this.requireSession().data.tokens.refreshToken;
    try {
      const response = await this.http.request('/auth/refresh', authSessionResponseSchema, {
        method: 'POST',
        requestId: nextRequestId('web-refresh'),
        body: { refreshToken },
      });
      this.assertAuthorizationEpoch(authorizationEpoch);
      this.accept(response);
      const token = response.data.tokens.accessToken;
      if (token === null) throw new WebApiError('MEMBERSHIP_REQUIRED', '请先选择机构身份。', 401);
      return token;
    } catch (error: unknown) {
      if (authorizationEpoch === this.authorizationEpoch) {
        this.clear(userErrorMessage(error, '登录已失效，请重新登录。'));
      }
      throw error;
    }
  }

  private requireSession(): AuthSessionResponse {
    const session = this.stateValue.session;
    if (session === null) throw new WebApiError('AUTH_REQUIRED', '请先登录。', 401);
    return session;
  }

  private accept(session: AuthSessionResponse): void {
    const previousScope = this.sessionScope(this.stateValue.session);
    const nextScope = this.sessionScope(session);
    if (previousScope !== nextScope) this.authorizationEpoch += 1;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      // The in-memory authenticated session remains usable when storage is unavailable.
    }
    this.update({ session, pending: false, error: null });
  }

  private clear(error: string | null = null): void {
    this.authorizationEpoch += 1;
    this.refreshOperation = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage restrictions while clearing the in-memory session.
    }
    this.update({ session: null, pending: false, error });
  }

  private sessionScope(session: AuthSessionResponse | null): string | null {
    if (session === null) return null;
    return `${session.data.user.userId}:${session.data.tokens.selectedMembershipId ?? ''}`;
  }

  private assertAuthorizationEpoch(expected: number): void {
    if (expected !== this.authorizationEpoch) {
      throw new WebApiError('AUTH_SESSION_CHANGED', '登录账号已切换，旧请求已取消。', 401);
    }
  }

  private fail(error: unknown): void {
    this.update({
      ...this.stateValue,
      pending: false,
      error: userErrorMessage(error, '操作失败，请重试。'),
    });
  }

  private update(state: IdentityState): void {
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
